import React from "react";
import { motion } from "framer-motion";
import MatrixBackground from "../components/MatrixBackground";
import { MapPinIcon, EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/solid";

const contactItems = [
  {
    icon: MapPinIcon,
    label: "Location",
    value: "Varanasi, Uttar Pradesh, India",
    accent: "text-green-400",
    bg: "rgba(0,255,153,0.06)",
    border: "rgba(0,255,153,0.15)",
  },
  {
    icon: EnvelopeIcon,
    label: "Email",
    value: "singh.pankaj@live.com",
    href: "mailto:singh.pankaj@live.com",
    accent: "text-cyan-400",
    bg: "rgba(6,182,212,0.06)",
    border: "rgba(6,182,212,0.15)",
  },
  {
    icon: PhoneIcon,
    label: "Phone",
    value: "+91 (735) 544-5118",
    href: "tel:+917355445118",
    accent: "text-purple-400",
    bg: "rgba(168,85,247,0.06)",
    border: "rgba(168,85,247,0.15)",
  },
];

const Contact = () => {
  return (
    <div className="min-h-screen font-mono p-6 relative flex items-center justify-center bg-[#050a0e]">
      <MatrixBackground />

      {/* Ambient orbs */}
      <div
        className="absolute top-1/3 left-1/4 w-64 h-64 rounded-full opacity-6 pointer-events-none z-[1]"
        style={{
          background: "radial-gradient(circle, #00ff99, transparent 70%)",
          filter: "blur(70px)",
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-5 pointer-events-none z-[1]"
        style={{
          background: "radial-gradient(circle, #06b6d4, transparent 70%)",
          filter: "blur(80px)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: -24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-xl"
      >
        {/* Card */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: "rgba(8,18,32,0.85)",
            border: "1px solid rgba(0,255,153,0.2)",
            boxShadow:
              "0 24px 64px rgba(0,0,0,0.6), 0 0 40px rgba(0,255,153,0.05)",
          }}
        >
          {/* Top gradient accent */}
          <div
            className="h-px"
            style={{
              background: "linear-gradient(90deg, #00ff99, #06b6d4, #a855f7)",
            }}
          />

          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="text-sm tracking-[0.15em] text-cyan-400/60 font-mono mb-3">
                $ cat ~/contact_info.yml
              </div>
              <h1 className="text-4xl md:text-5xl font-bold gradient-text tracking-tight">
                Get in Touch
              </h1>
              <p className="text-sm text-slate-400/80 mt-3 tracking-wide">
                Open to opportunities and collaborations
              </p>
            </div>

            {/* Contact items */}
            <div className="space-y-4">
              {contactItems.map((item, i) => {
                const Icon = item.icon;
                const content = (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.15, duration: 0.5 }}
                    whileHover={{ x: 4 }}
                    className="flex items-center gap-4 p-4 rounded-xl transition-all duration-300"
                    style={{
                      background: item.bg,
                      border: `1px solid ${item.border}`,
                    }}
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${item.accent}`}
                      style={{
                        background: item.bg,
                        border: `1px solid ${item.border}`,
                      }}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div
                        className={`text-xs tracking-widest uppercase ${item.accent} opacity-70 mb-0.5`}
                      >
                        {item.label}
                      </div>
                      <div className="text-sm text-slate-100 font-medium">
                        {item.value}
                      </div>
                    </div>
                  </motion.div>
                );

                return item.href ? (
                  <a
                    key={i}
                    href={item.href}
                    className="block hover:no-underline"
                  >
                    {content}
                  </a>
                ) : (
                  content
                );
              })}
            </div>

            {/* Footer note */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="text-xs text-center text-slate-500/70 mt-8 tracking-wide"
            >
              &gt; Available for freelance &amp; full-time roles_
            </motion.p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;
